import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Search, Gift, Plus, X, Trophy } from 'lucide-react';
import { raffleAPI } from '@/lib/api';

/**
 * Create / edit a raffle. Agents are auto-assigned to raffles they create;
 * KR admins (and agents) can assign additional agents from the picker.
 */
export default function RaffleFormDialog({ open, onOpenChange, raffle, onDone }) {
  const editing = !!raffle;
  const [name, setName] = useState('');
  const [prize, setPrize] = useState('');
  const [prizes, setPrizes] = useState([]);
  const [prizeDraft, setPrizeDraft] = useState('');
  const [drawDate, setDrawDate] = useState('');
  const [assigned, setAssigned] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agentQuery, setAgentQuery] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(raffle?.name || '');
    setPrize(raffle?.prize || '');
    setPrizes(raffle?.prizes || []);
    setPrizeDraft('');
    setDrawDate(raffle?.draw_date ? raffle.draw_date.slice(0, 10) : '');
    setAssigned((raffle?.assigned_agent_npns) || []);
    setAgentQuery('');
    raffleAPI.assignableAgents().then((r) => setAgents(r.data.agents || [])).catch(() => {});
  }, [open, raffle]);

  const filtered = useMemo(() => {
    const q = agentQuery.trim().toLowerCase();
    const base = q ? agents.filter((a) => (a.name || '').toLowerCase().includes(q) || (a.npn || '').includes(q)) : agents;
    return base.slice(0, 60);
  }, [agents, agentQuery]);

  const toggle = (npn) => setAssigned((prev) => (prev.includes(npn) ? prev.filter((n) => n !== npn) : [...prev, npn]));

  const addPrize = () => {
    const v = prizeDraft.trim();
    if (!v) return;
    if (prizes.some((p) => p.toLowerCase() === v.toLowerCase())) { setPrizeDraft(''); return; }
    setPrizes((prev) => [...prev, v]);
    setPrizeDraft('');
  };
  const removePrize = (i) => setPrizes((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!name.trim() || !prize.trim()) { toast.error('Name and prize are required'); return; }
    setSaving(true);
    const payload = {
      name: name.trim(),
      prize: prize.trim(),
      prizes,
      draw_date: drawDate ? new Date(drawDate).toISOString() : null,
      assigned_agent_npns: assigned,
    };
    try {
      if (editing) await raffleAPI.update(raffle.raffle_id, payload);
      else await raffleAPI.create(payload);
      toast.success(editing ? 'Raffle updated' : 'Raffle created');
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="raffle-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift size={16} className="text-amber-600" /> {editing ? 'Edit Raffle' : 'New Raffle'}
          </DialogTitle>
          <DialogDescription>Set the prize and assign agents who can enter their leads into this raffle.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Raffle name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. AEP Kickoff Raffle" data-testid="raffle-name-input" />
          </div>
          <div>
            <Label className="text-xs">Prize / gift</Label>
            <Input value={prize} onChange={(e) => setPrize(e.target.value)} placeholder="e.g. $100 Visa Gift Card" data-testid="raffle-prize-input" />
          </div>
          <div>
            <Label className="text-xs">Prize list (optional)</Label>
            <p className="text-[11px] text-slate-400 mb-1">Add each prize (1st, 2nd, 3rd…). You can then assign them to winners with a single tap after the draw.</p>
            <div className="flex items-center gap-2">
              <Input
                value={prizeDraft}
                onChange={(e) => setPrizeDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPrize(); } }}
                placeholder="e.g. 1st prize — iPad"
                className="h-9"
                data-testid="raffle-prize-list-input"
              />
              <Button type="button" variant="outline" size="sm" className="h-9 shrink-0" onClick={addPrize} data-testid="raffle-prize-add-btn">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            {prizes.length > 0 && (
              <ol className="mt-2 space-y-1" data-testid="raffle-prize-list">
                {prizes.map((p, i) => (
                  <li key={`${p}-${i}`} className="flex items-center gap-2 text-sm rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5" data-testid={`raffle-prize-item-${i}`}>
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-amber-500 text-white text-[11px] font-bold shrink-0">{i + 1}</span>
                    <span className="flex items-center gap-1.5 text-amber-900"><Trophy size={13} className="text-amber-600" /> {p}</span>
                    <button type="button" className="ml-auto text-amber-700 hover:text-rose-600" onClick={() => removePrize(i)} data-testid={`raffle-prize-remove-${i}`}><X size={14} /></button>
                  </li>
                ))}
              </ol>
            )}
          </div>
          <div>
            <Label className="text-xs">Draw date (optional)</Label>
            <Input type="date" value={drawDate} onChange={(e) => setDrawDate(e.target.value)} data-testid="raffle-drawdate-input" />
          </div>
          <div>
            <Label className="text-xs">Assigned agents</Label>
            <p className="text-[11px] text-slate-400 mb-1">Assigned agents can enter their leads into this raffle (you are added automatically).</p>
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input value={agentQuery} onChange={(e) => setAgentQuery(e.target.value)} placeholder="Search agents…" className="pl-8 h-8 text-sm" data-testid="raffle-agent-search" />
            </div>
            <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
              {filtered.length === 0 && <p className="p-3 text-xs text-slate-400">No agents match.</p>}
              {filtered.map((a) => (
                <label key={a.npn} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-sm" data-testid={`raffle-agent-${a.npn}`}>
                  <Checkbox checked={assigned.includes(a.npn)} onCheckedChange={() => toggle(a.npn)} />
                  <span>{a.name}</span>
                  <span className="text-[11px] text-slate-400 ml-auto">#{a.npn}</span>
                </label>
              ))}
            </div>
            {assigned.length > 0 && <p className="text-[11px] text-emerald-700 mt-1">{assigned.length} agent{assigned.length === 1 ? '' : 's'} assigned</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="raffle-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} {editing ? 'Save' : 'Create raffle'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
